import { useEffect, useState } from "react";
import { NavLink, Outlet } from "react-router-dom";
import { HelpButton } from "@/components/HelpButton";
import { AlertsBell } from "@/components/AlertsBell";
import { InstallAppButton } from "@/components/InstallAppButton";
import { api } from "@/lib/api";
import { LayoutDashboard, MessageCircle, ChefHat, Wheat, ClipboardList, Package, Users, Truck, Wallet, Sparkles, UserCog, Settings, LogOut, Menu, UserPlus, Megaphone, Bug, Building2, PhoneCall, Receipt, Store, Rocket, Landmark, KeyRound } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { ChangePasswordForm } from "@/components/ChangePasswordForm";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { cn } from "@/lib/utils";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, roles: ["admin"], id: "dashboard" },
  { to: "/confirmations", label: "WhatsApp Orders", icon: MessageCircle, roles: ["admin", "production"], id: "confirmations" },
  { to: "/production", label: "Production", icon: ChefHat, roles: ["admin", "production"], id: "production" },
  { to: "/materials", label: "Raw Materials", icon: Wheat, roles: ["admin", "production"], id: "materials" },
  { to: "/purchasing", label: "Suppliers & POs", icon: ClipboardList, roles: ["admin", "production"], id: "purchasing" },
  { to: "/products", label: "Products & Recipes", icon: Package, roles: ["admin"], id: "products" },
  { to: "/customers", label: "Customers", icon: Users, roles: ["admin"], id: "customers" },
  { to: "/deliveries", label: "Deliveries", icon: Truck, roles: ["admin"], id: "deliveries" },
  { to: "/credit", label: "Credit & Payments", icon: Wallet, roles: ["admin"], id: "credit" },
  { to: "/stall-sales", label: "Stall Sales (B2P)", icon: Store, roles: ["admin", "production"], id: "stall-sales" },
  { to: "/insights", label: "AI Insights", icon: Sparkles, roles: ["admin"], id: "insights" },
  { to: "/leads", label: "Leads", icon: PhoneCall, roles: ["admin", "sales"], id: "leads" },
  { to: "/my-customers", label: "Customers", icon: Users, roles: ["sales"], id: "my-customers" },
  { to: "/gst", label: "GST", icon: Receipt, roles: ["admin", "accountant"], id: "gst" },
  { to: "/accounts", label: "Accounts & Salaries", icon: Landmark, roles: ["admin", "accountant"], id: "accounts" },
  { to: "/signups", label: "Member Sign-ups", icon: UserPlus, roles: ["admin"], id: "signups" },
  { to: "/offers", label: "Festival Offers", icon: Megaphone, roles: ["admin"], id: "offers" },
  { to: "/launches", label: "Product Launch", icon: Rocket, roles: ["admin"], id: "launches" },
  { to: "/societies", label: "Societies", icon: Building2, roles: ["admin"], id: "societies" },
  { to: "/bugs", label: "QA Bugs", icon: Bug, roles: ["admin", "qa"], id: "bugs" },
  { to: "/team", label: "Team", icon: UserCog, roles: ["admin"], id: "team" },
  { to: "/settings", label: "Settings", icon: Settings, roles: ["admin"], id: "settings" },
];

function NavBody({ onNavigate }) {
  const { user, logout } = useAuth();
  const [brand, setBrand] = useState({});
  const [pwOpen, setPwOpen] = useState(false);
  useEffect(() => { api.get("/brand").then((r) => setBrand(r.data)).catch(() => {}); }, []);
  return (
    <div className="flex h-full flex-col bg-[#0F382C] text-emerald-50">
      <div className="px-6 pt-7 pb-8 flex items-start justify-between gap-2">
        <div className="flex items-center gap-3 min-w-0">
          {brand.logo_url && <img src={brand.logo_url} alt="logo" className="h-11 w-11 rounded-xl bg-[#FBF9F4] object-contain p-1" data-testid="sidebar-logo" />}
          <div className="min-w-0">
            <div className="font-display text-xl font-extrabold tracking-tight truncate" data-testid="sidebar-brand">{brand.business_name || <>Batter<span className="text-[#F2B880]">Flow</span></>}</div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-emerald-200/70 mt-0.5 truncate">{brand.business_name ? "The Batter House" : "Batter business OS"}</div>
          </div>
        </div>
        {user?.role === "admin" && <AlertsBell dark />}
      </div>
      <nav className="flex-1 space-y-1 px-3 overflow-y-auto">
        {NAV.filter((n) => n.roles.includes(user?.role)).map((n) => (
          <NavLink key={n.to} to={n.to} end={n.to === "/"} onClick={onNavigate} data-testid={`nav-${n.id}`}
            className={({ isActive }) => cn(
              "flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors",
              isActive ? "bg-[#FBF9F4] text-[#0F382C] shadow-sm" : "text-emerald-100/85 hover:bg-white/10"
            )}>
            <n.icon className="h-4 w-4" />
            {n.label}
          </NavLink>
        ))}
      </nav>
      <div className="m-3 rounded-2xl bg-white/5 p-4">
        <div className="text-sm font-semibold" data-testid="current-user-name">{user?.name}</div>
        <div className="text-xs capitalize text-emerald-200/70">{user?.role}</div>
        <InstallAppButton variant="ghost" className="mt-3 w-full justify-start bg-[#F2B880]/15 text-[#F2B880] hover:bg-[#F2B880]/25 hover:text-[#F2B880]" testId="sidebar-install-app-btn" />
        <Button variant="ghost" size="sm" onClick={() => setPwOpen(true)} className="mt-3 w-full justify-start text-emerald-100 hover:bg-white/10 hover:text-white" data-testid="change-password-btn">
          <KeyRound className="mr-2 h-4 w-4" /> Change password
        </Button>
        <Dialog open={pwOpen} onOpenChange={setPwOpen}>
          <DialogContent className="max-w-sm" data-testid="change-password-dialog">
            <DialogHeader><DialogTitle className="font-display">Change password</DialogTitle><DialogDescription>Choose a new password that only you know.</DialogDescription></DialogHeader>
            {pwOpen && <ChangePasswordForm onDone={() => setPwOpen(false)} />}
          </DialogContent>
        </Dialog>
        <Button variant="ghost" size="sm" onClick={logout} className="mt-1 w-full justify-start text-emerald-100 hover:bg-white/10 hover:text-white" data-testid="logout-btn">
          <LogOut className="mr-2 h-4 w-4" /> Log out
        </Button>
      </div>
    </div>
  );
}

export default function Layout() {
  const [open, setOpen] = useState(false);
  return (
    <div className="min-h-screen bg-[#FBF9F4]">
      <aside className="fixed inset-y-0 left-0 hidden w-64 lg:block">
        <NavBody />
      </aside>
      <header className="sticky top-0 z-30 flex items-center justify-between border-b border-[#E7E2D6] bg-[#FBF9F4]/90 px-4 py-3 backdrop-blur-md lg:hidden">
        <div className="font-display text-xl font-extrabold text-[#0F382C]">Batter<span className="text-[#D96B43]">Flow</span></div>
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild>
            <Button variant="outline" size="icon" data-testid="mobile-menu-btn"><Menu className="h-5 w-5" /></Button>
          </SheetTrigger>
          <SheetContent side="left" className="w-72 p-0 border-0">
            <NavBody onNavigate={() => setOpen(false)} />
          </SheetContent>
        </Sheet>
      </header>
      <main className="lg:pl-64">
        <div className="mx-auto max-w-7xl p-4 sm:p-6 lg:p-10">
          <Outlet />
        </div>
      </main>
      <HelpButton />
    </div>
  );
}
